<template>
  <TextInputBase
    id="google-city-input"
    ref="textInputRef"
    :model-value="searchString"
    :placeholder="label"
    @update:model-value="onChange"
    @focusout="onFocusOut"
  />
</template>

<script setup lang="ts">
import { Loader } from '@googlemaps/js-api-loader'
import { GqlClientName } from '@ramp106/gql/clientNames'
import { TextInputBase } from '@ramp106/omrjs-core-ui/deprecated'
import * as Sentry from '@sentry/nuxt'
import { onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import type { Location } from '@/gql/jobs/schema.generated'
import { getErrorContext } from '@/helpers/errors'

import { useRuntimeConfig } from '#imports'
import { useFindOrCreateLocationMutation } from '~/gql/jobs/requests.generated'
import { useNotificationStore } from '~/store/notification'

interface GoogleCityInputProps {
  label: string
  name?: string
}

const {
  public: { googleMapsApiKey },
} = useRuntimeConfig()

const { mutate, onDone, onError } = useFindOrCreateLocationMutation({ clientId: GqlClientName.Jobs })

const { t } = useI18n()
const store = useNotificationStore()

const emit = defineEmits<{
  update: [location: Location | Record<string, never>]
}>()

const props = withDefaults(defineProps<GoogleCityInputProps>(), {
  name: '',
})

const searchString = ref(props.name)
const textInputRef = ref<InstanceType<typeof TextInputBase>>()

watch(
  () => props.name,
  (newValue) => {
    searchString.value = newValue ?? ''
  },
)

const onChange = (newSearchString: string) => {
  searchString.value = newSearchString
}

const onFocusOut = () => {
  const isFieldChanged = searchString.value !== props.name

  if (!isFieldChanged) return

  resetInput()
}

const resetInput = () => {
  emit('update', {})

  searchString.value = ''

  // For some reason the input needs to be reset as well, otherwise consecutive resets are not working
  if (textInputRef.value?.inputRef) {
    textInputRef.value.inputRef.value = ''
  }
}

const executeFindOrCreateLocation = async (name: string, placeIdGoogle: string) => {
  mutate({
    name,
    placeIdGoogle,
  })
}

onDone((res) => {
  const data = res.data?.findOrCreateLocation

  if (data?.location) {
    emit('update', {
      id: data.location.id,
      name: data.location.name,
      placeIdGoogle: data.location.placeIdGoogle,
    } as Location)

    searchString.value = data.location.name
  }
})

onError((error) => {
  store.add({
    type: 'error',
    message: t('create.location.error'),
  })

  Sentry.captureException(error, getErrorContext())
})

const sanitizeAddress = (address: string) => {
  const addressWithoutCountry = address.split(', ').slice(0, -1).join(', ')
  const zipCodeMatch = addressWithoutCountry.match(/^([0-9]+ )/)
  if (zipCodeMatch) {
    return addressWithoutCountry.slice(zipCodeMatch[0].length)
  } else {
    return addressWithoutCountry
  }
}

onMounted(() => {
  const options = {
    types: ['(cities)'],
  }

  if (!textInputRef.value?.inputRef) return

  const loader = new Loader({
    apiKey: googleMapsApiKey,
    version: 'weekly',
    libraries: ['places'],
  })

  loader.load().then(() => {
    const place = new google.maps.places.Autocomplete(textInputRef.value!.inputRef!, options)

    google.maps.event.addListener(place, 'place_changed', function () {
      const { place_id: placeIdGoogle, formatted_address: name } = place.getPlace()

      if (name && placeIdGoogle) {
        executeFindOrCreateLocation(sanitizeAddress(name), placeIdGoogle)
      }
    })

    // This is terrible but initializing the Google Places API on an input field resets the autcomplete attribute to 'off'
    // And the 'off' setting in ignored: https://stackoverflow.com/q/12374442/2771889
    // Have to manually reset it to any different value after some delay
    setTimeout(() => {
      if (textInputRef.value?.inputRef) {
        textInputRef.value.inputRef.autocomplete = 'nope' as HTMLInputElement['autocomplete']
      }
    }, 1000)
  })
})
</script>
